import React, { Component } from 'react';
import { Alert } from 'react-bootstrap';
import vis from 'vis-network/dist/vis-network.min.js';
import 'vis-network/dist/dist/vis-network.min.css';

import Theme from 'context/theme';

import './graphCtrlVis.css';

/**
 * THE vis-network BRIDGE.
 *
 * vis draws the graph into a <canvas>. A canvas takes fill and stroke as strings passed to the 2D
 * context, and `ctx.fillStyle = 'var(--x)'` is neither an error nor a colour — the assignment is
 * silently ignored and the previous fill kept. It also does not repaint when a stylesheet changes.
 * So this bridge is two literal palettes plus `network.setOptions()` on a theme change, the same
 * shape mermaid's takes and for the same reason.
 *
 * EVERY VALUE BELOW IS THE WORKFLOW EDITOR'S NODE, RESTATED FOR A CANVAS. The reference is
 * flowkit/flowkit.css (`.bh-flow-node`) and flowkit/flowEdge.js; the measured property-for-property
 * comparison is docs/specs/2026-10-14-graph-onnx-node-parity.md. The token each literal comes from
 * is named beside it and asserted against src/theme.css by graphCtrlVis.test.js, because a literal
 * copied out of a token is a literal that goes stale in silence.
 *
 * THE ACCENT BAR IS DRAWN, NOT BORROWED. vis's box shape has ONE border and paints it on all four
 * sides; the reference has no border at all and a 4px bar down its left edge. An outline is not
 * that bar in a different width, it is a different mark — which is what made every node here read
 * as a boxed node and every selected one as a dark ring. So `borderWidth` is 0 and `drawAccentBars`
 * paints the bar on the frame vis has just finished.
 *
 * THREE THINGS THE CANVAS STILL CANNOT SAY, and what stands in for each:
 *
 *   the dark shadow's ring   --bh-shadow-sm carries `0 0 0 1px --bh-border` in dark and nothing of
 *                            the kind in light; vis's shadow option is one colour plus x/y/size and
 *                            has no ring. Nothing stands in for it: the only four-sided mark vis can
 *                            draw is a border, which is the mark this surface was rejected for. It
 *                            measures 1.11:1 against the plate, where the border that used to stand
 *                            in for it measured 1.59:1 — a visible box, in the wrong token.
 *   the selected title ink   a label's colour is an OPTION, not a state, so it cannot lift to
 *                            --bh-text-strong on selection without making `chosen.label` a function
 *                            — which would put a function in the options object the tests below
 *                            serialise. The resting ink is used in both states.
 *   quoted font families     `ctx.font = …` is assigned through `fontString.replace(/"/g, "")`
 *                            (vis-network.js, Label.getFormattingValues), so a quoted multi-word
 *                            family arrives unquoted and invalidates the whole shorthand — Chrome
 *                            then keeps the previous font and nothing reports it. The stacks below
 *                            are the reference's with every quoted family dropped, which on macOS
 *                            and Windows resolves to the same face.
 */

// The app's own stacks, quote-free — see the header. The reference's full stacks are in
// bootstrap.theme.css (body) and flowkit.css (`.bh-flow-node-subtitle`).
const NODE_FONT_FACE = "-apple-system, system-ui, Roboto, Arial, sans-serif";
const MONO_FONT_FACE = "ui-monospace, SFMono-Regular, Menlo, monospace";

// flowkit.css's own geometry, so the two surfaces can only drift together.
const NODE_PADDING = { top: 10, right: 13, bottom: 11, left: 14 };   // .bh-flow-node padding
const TITLE_SIZE = 12.5;                                             // .bh-flow-node-title
const SUBTITLE_SIZE = 10.5;                                          // .bh-flow-node-subtitle
const EDGE_LABEL_SIZE = 10;                                          // .react-flow__edge-text
const NODE_BAR_WIDTH = 4;                                            // .bh-flow-node::before
const EDGE_WIDTH = 1;                                                // flowEdge.js, at rest
// flowEdge.js's selected stroke. Also the width this file gives an edge the last diff touched: the
// reference draws exactly two widths, so this surface draws exactly two as well.
const EDGE_WIDTH_EMPHASIS = 1.5;
// flowEdge.js's <marker> is 11.2 x 5.6 user-space units. vis has no marker: it derives an
// endpoint's length from `15 * scaleFactor + 3 * lineWidth` and draws the triangle from x=0.02L to
// x=-1L, so 1.02 of it (vis-network.js, EndPoints.Triangle.draw).
const ARROW_LENGTH = 11.2;
const ARROW_SCALE = (ARROW_LENGTH / 1.02 - 3 * EDGE_WIDTH) / 15;

// The two palettes, literal for literal, each entry carrying the token it is a copy of. vis cannot
// read a variable, so these cannot be var() — see the header.
const VIS_PALETTE = {
  light: {
    nodeBg: '#f2f4f6',                // --bh-fill: the plate .bh-flow-node paints
    nodeBar: '#ced4da',               // --bh-border-strong: .bh-flow-node::before
    nodeHighlightBg: '#e8ebee',       // --bh-fill-strong: .bh-flow-node-selected
    nodeHighlightBar: '#374151',      // --bh-text-secondary: .bh-flow-node-selected::before
    nodeInk: '#212529',               // --bh-text: .bh-flow-node-title
    subInk: '#6a737d',                // --bh-text-muted: .bh-flow-node-subtitle
    edgeInk: '#9aa3af',               // --bh-edge: the stroke, the arrowhead and the edge label
    edgeSelectedInk: '#3d4756',       // --bh-edge-selected
    edgeLabelHalo: '#ffffff',         // --bh-canvas: the label's ground, restated
    shadow: 'rgba(20, 30, 45, 0.1)',  // the colour inside --bh-shadow-sm
    recentBg: '#e7f1ff'               // --bh-accent-tint-2: the "changed in the last diff" plate
  },
  dark: {
    nodeBg: '#26262b',                // --bh-fill
    nodeBar: '#45454d',               // --bh-border-strong
    nodeHighlightBg: '#35353c',       // --bh-fill-strong
    nodeHighlightBar: '#d4d4d8',      // --bh-text-secondary
    nodeInk: '#e4e4e7',               // --bh-text
    subInk: '#a1a1aa',                // --bh-text-muted
    edgeInk: '#5c5c66',               // --bh-edge
    edgeSelectedInk: '#c2c2c9',       // --bh-edge-selected
    edgeLabelHalo: '#171719',         // --bh-canvas
    shadow: 'rgba(0, 0, 0, 0.5)',     // the colour inside --bh-shadow-sm
    recentBg: '#272d39'               // --bh-accent-tint-2
  }
};

/**
 * `.bh-flow-node::before` — the 4px bar down a node's left edge — as a canvas pass.
 *
 * THE WHOLE PASS, so that what the probe page drives is what the component calls: the box vis
 * measured for a node IS its plate, and the selection is read off the network rather than tracked,
 * because a drag or a programmatic select changes it without passing through onSelectContent.
 *
 * Run on vis's "afterDrawing", where the context already carries the view transform, so the boxes
 * are in graph coordinates. The width is divided by the scale for the same reason vis divides its
 * own border width by it: a bar that zooms with the graph is not the 4 CSS px the reference draws.
 *
 * A node vis has not built yet has no box — a DataSet mid-update has them — and is skipped rather
 * than filled at NaN, which is a silently absent bar.
 */
function drawAccentBars(ctx, network, nodeIds, palette) {
  const width = NODE_BAR_WIDTH / network.getScale();
  const selected = new Set(network.getSelectedNodes());
  for ( const nodeId of nodeIds ) {
    const box = network.getBoundingBox(nodeId);
    if ( !box ) {
      continue;
    }
    ctx.fillStyle = selected.has(nodeId) ? palette.nodeHighlightBar : palette.nodeBar;
    ctx.fillRect(box.left, box.top, width, box.bottom - box.top);
  }
}

function visOptions(palette) {
  return {
    physics: {
      enabled: true,
      stabilization: {
        enabled: true,
        fit: true
      }
    },
    layout: {
      randomSeed: 2
    },
    nodes: {
      shape: "box",
      // `label: false` keeps vis from emboldening the whole label on selection, which would flatten
      // the <b>/<code> split the default templates draw the title and subtitle with.
      chosen: {
        label: false
      },
      shapeProperties: {
        borderRadius: 0
      },
      // NO BORDER. The reference has none; its left edge is a 4px ::before bar, which drawAccentBars
      // paints. vis guards its stroke on `borderWidth > 0` (NodeBase.performStroke), so 0 draws
      // nothing rather than falling back to the context's previous line width.
      borderWidth: 0,
      borderWidthSelected: 0,
      font: {
        multi: true,
        face: NODE_FONT_FACE,
        size: TITLE_SIZE,
        align: "left",
        color: palette.nodeInk,
        // `<code>` in a label — which the default vertex template uses for the id — takes this font.
        // Left unset, it is vis's own 15px courier with a +2 vertical nudge.
        mono: {
          face: MONO_FONT_FACE,
          size: SUBTITLE_SIZE,
          color: palette.subInk,
          vadjust: 0
        }
      },
      margin: {
        top: NODE_PADDING.top,
        bottom: NODE_PADDING.bottom,
        left: NODE_PADDING.left,
        right: NODE_PADDING.right
      },
      // Plates only: the two bar inks are drawn by drawAccentBars, not by vis. A `border` here would
      // be a colour for a stroke nothing paints.
      color: {
        background: palette.nodeBg,
        highlight: {
          background: palette.nodeHighlightBg
        }
      },
      shadow: {
        enabled: true,
        color: palette.shadow,
        x: 0,
        y: 1,
        size: 3
      },
      // 0.8 multiplied every colour below by itself, so no palette could match the reference while
      // it stood. A .bh-flow-node is opaque.
      opacity: 1
    },
    edges: {
      // SET, and that is the change: vis's default `inherit: "from"` draws an edge in its SOURCE
      // NODE's border colour, and the reference draws the node's edge and the node's outline in two
      // different tokens (--bh-edge and --bh-border-strong). Inheritance cannot express two, and it
      // cannot express a selected edge at all — it would hand a selected edge the node's HIGHLIGHT
      // border. Setting this turns inheritance off for good; the two tokens are the point.
      color: {
        color: palette.edgeInk,
        highlight: palette.edgeSelectedInk,
        inherit: false
      },
      width: EDGE_WIDTH,
      // ADDED to width when an edge is selected, so 0.5 is how 1 becomes the reference's 1.5.
      selectionWidth: EDGE_WIDTH_EMPHASIS - EDGE_WIDTH,
      font: {
        size: EDGE_LABEL_SIZE,
        face: NODE_FONT_FACE,
        color: palette.edgeInk,
        strokeColor: palette.edgeLabelHalo
      },
      shadow: {
        enabled: false
      },
      arrows: {
        to: {
          enabled: true,
          // vis's default "arrow" is a dart with a notch; the reference's marker is a plain
          // triangle, which this is the only endpoint type to draw.
          type: "triangle",
          scaleFactor: ARROW_SCALE
        }
      }
    }
  };
}

// Exported purely for unit testing: `visOptions` is where a palette key can silently stop being
// read, and VIS_PALETTE is where one half can grow an entry the other lacks. The geometry constants
// are what the parity spec's table is read off. None is reachable from the component's public
// surface.
export {
  VIS_PALETTE, visOptions, drawAccentBars, ARROW_LENGTH, ARROW_SCALE, EDGE_WIDTH,
  EDGE_WIDTH_EMPHASIS, NODE_PADDING, TITLE_SIZE, SUBTITLE_SIZE, EDGE_LABEL_SIZE, NODE_BAR_WIDTH,
  NODE_FONT_FACE, MONO_FONT_FACE
};


export default class GraphControl extends Component {
  constructor(props) {
    super(props);

    this.uiVertexIndex = new Map();
    this.network = null;
    this.nodes = new vis.DataSet([]);
    this.edges = new vis.DataSet([]);

    this.props.shell.graphCtrlInterface = {
      incorporateDiff: this.incorporateDiff,
      refreshAppearance: this.refreshAppearance,
      activate: this.activate,
      clear: this.clear
    };
  }

  /**
   * Control interface
   */

  activate = () => {
    this.adjustUiToRecentUpdates();
  }

  incorporateDiff = () => {
    this.incorporateDiffCore();
    this.adjustUiToRecentUpdates();
  }

  refreshAppearance = () => {
    this.refreshAppearanceCore();
    this.adjustUiToRecentUpdates();
  }

  clear = () => {
    this.edges.clear();
    this.nodes.clear();
    this.adjustUiToRecentUpdates();
  }

  /**
   * Callbacks
   */

  onSelectContent = (params) => {
    for (var nodeId of params.nodes) {
      this.applyNodeSelection(nodeId);
      return;
    }

    for (var edgeId of params.edges) {
      this.applyEdgeSelection(edgeId);
      return;
    }
  }

  applyNodeSelection(nodeId) {
    let uiVertex = this.nodes.get(nodeId);
    let vertex = uiVertex.vertex;
    this.props.shell.shellInterface.updateDetails(true, "vertex", vertex);
  }

  applyEdgeSelection(edgeId) {
    let uiEdge = this.edges.get(edgeId);
    let edge = uiEdge.edge;
    this.props.shell.shellInterface.updateDetails(true, "edge", edge);
  }

  /**
   * Rendering
   */

  createGraph() {
    // create a network
    var container = document.getElementById("explore-preview-graph");
    var data = {
      nodes: this.nodes,
      edges: this.edges
    };
    this.network = new vis.Network(container, data, visOptions(VIS_PALETTE[Theme.resolved()]));
    this.applyPalette(Theme.resolved());

    // A theme change repaints the canvas: new options for the shared node and edge styling, and a
    // pass over the DataSet for the per-node colours the diff highlighting writes directly into it.
    this.unsubscribeTheme = Theme.subscribe(() => {
      if ( !this.network ) {
        return;
      }
      const theme = Theme.resolved();
      this.network.setOptions(visOptions(VIS_PALETTE[theme]));
      this.applyPalette(theme);
      this.network.redraw();
    });

    // Setup callbacks
    this.network.on("select", this.onSelectContent);
    // The accent bar, on every frame: vis has no option for a one-sided edge, so this draws it over
    // the frame vis has just finished. Selection is read from the network rather than tracked here —
    // a hover, a drag or a programmatic select all change it without going through onSelectContent.
    this.network.on("afterDrawing", this.paintAccentBars);
  }

  paintAccentBars = (ctx) => {
    if ( this.network && this.palette ) {
      drawAccentBars(ctx, this.network, this.nodes.getIds(), this.palette);
    }
  }

  /**
   * The two per-node colour styles the diff highlighting assigns are written into the DataSet, not
   * into options, so `setOptions` alone cannot reach a node that is already on screen — it would
   * keep its old background while everything around it flipped. Re-stamp them, preserving which
   * nodes are "recent" by comparing against the palette that was in force a moment ago.
   */
  applyPalette(theme) {
    const palette = VIS_PALETTE[theme];
    const wasRecent = this.recentNodeColorStyle;
    // What the bar pass reads. Kept here rather than resolved per frame, and it is why a theme
    // change has to reach this method even when the DataSet is empty.
    this.palette = palette;
    this.normalNodeColorStyle = { background: palette.nodeBg };
    this.recentNodeColorStyle = { background: palette.recentBg };
    if ( !wasRecent ) {
      return;                                   // first call: nothing is in the DataSet yet
    }
    const updates = [];
    for ( const nodeId of this.nodes.getIds() ) {
      const current = this.nodes.get(nodeId).color;
      const isRecent = current && current.background === wasRecent.background;
      updates.push({
        id: nodeId,
        color: isRecent ? this.recentNodeColorStyle : this.normalNodeColorStyle
      });
    }
    this.nodes.update(updates);
  }

  incorporateDiffCore() {
    // Clear previous node highlighting
    var notesToUpdate = [];
    for (var nodeId of this.nodes.getIds()) {
      notesToUpdate.push({
        id: nodeId,
        color: this.normalNodeColorStyle
      });
    }
    this.nodes.update(notesToUpdate);

    // Clear previous edge highlighting
    var edgesToUpdate = [];
    for (var edgeId of this.edges.getIds()) {
      edgesToUpdate.push({
        id: edgeId,
        width: EDGE_WIDTH
      });
    }
    this.edges.update(edgesToUpdate);

    if ( !this.props.shell.graph.mostRecentDiff ) {
      return;
    }

    // Process new nodes
    for (var nodeId of this.props.shell.graph.mostRecentDiff.nodes()) {
      // Skip nodes that are already present
      if ( this.nodes.get(nodeId) ) {
        // Highlight the node
        this.nodes.update([{
          id: nodeId,
          color: this.recentNodeColorStyle
        }]);
        continue;
      }

      // Add node
      const node = this.props.shell.graph.mostRecentDiff.node(nodeId);
      const LabelTemplate = this.findAppearanceTemplate("vertex", node);
      const nodeLabel = this.renderTemplate(LabelTemplate, {vertex: node});
      const uiVertex = {
        id: nodeId,
        label: nodeLabel,
        color: this.recentNodeColorStyle,
        vertex: node
      }
      this.nodes.add(uiVertex);
      this.uiVertexIndex.set(nodeId, uiVertex);
    }

    // Process new edges
    for (var edgeObj of this.props.shell.graph.mostRecentDiff.edges()) {
      // Skip edges that are already present
      const edge = this.props.shell.graph.mostRecentDiff.edge(edgeObj);
      if ( this.edges.get(edge.id) ) {
        // Highlight the edge
        this.edges.update([{
          id: edge.id,
          width: EDGE_WIDTH_EMPHASIS
        }]);
        continue;
      }

      // Add edge
      const edgeTemplate = this.findAppearanceTemplate("edge", edge)
      const edgeLabel = this.renderTemplate(edgeTemplate, {edge: edge});
      const uiEdge = {
        id: edge.id,
        from: edgeObj.v,
        to: edgeObj.w,
        width: EDGE_WIDTH_EMPHASIS,
        label: edgeLabel,
        edge: edge
      };
      this.edges.add(uiEdge);
    }
  }

  refreshAppearanceCore() {
    // Update nodes
    var notesToUpdate = [];
    for (var nodeId of this.nodes.getIds()) {
      const node = this.nodes.get(nodeId).vertex;
      const LabelTemplate = this.findAppearanceTemplate("vertex", node);
      const nodeLabel = this.renderTemplate(LabelTemplate, {vertex: node});
      notesToUpdate.push({
        id: nodeId,
        label: nodeLabel
      });
    }
    this.nodes.update(notesToUpdate);

    // Update edges
    var edgesToUpdate = [];
    for (var edgeId of this.edges.getIds()) {
      const edge = this.edges.get(edgeId).edge;
      const edgeTemplate = this.findAppearanceTemplate("edge", edge)
      const edgeLabel = this.renderTemplate(edgeTemplate, {edge: edge});
      edgesToUpdate.push({
        id: edgeId,
        label: edgeLabel
      });
    }
    this.edges.update(edgesToUpdate);
  }

  /**
   * `fit()` measures the container, and runs the statement after `forceUpdate()` — which React 18
   * has not committed yet. So the container's SIZE must never depend on this commit, nor on any
   * ancestor's view state. See docs/specs/2026-10-14-graph-canvas-always-sized.md.
   */
  adjustUiToRecentUpdates() {
    this.forceUpdate();
    this.network.fit();
  }

  /**
   * Appearance
   */

  findAppearanceTemplate(type, object) {
    // Default templates. `multi: true` reads <b> and <code> as vis's bold and mono fonts, which is
    // how a canvas label gets .bh-flow-node's title-over-subtitle split: the class is the title and
    // the record id the mono subtitle. A saved appearance template replaces this and is the user's.
    if ( "vertex" == type ) {
      var template = "<b>${vertex.class}</b>\n<code>${vertex.id}</code>";
    } else if ( "edge" == type ) {
      var template = "${edge.class}";
    } else {
      var template = "unknown";
    }

    // Templates from appearance store
    if ( this.props.shell.appearance ) {
      var appearanceItem = null
      if ( "vertex" == type ) {
        appearanceItem = this.props.shell.appearance.verticesIndexByClass[object["class"]];
      } else if ( "edge" == type ) {
        appearanceItem = this.props.shell.appearance.edgesIndexByClass[object["class"]];
      }
      if ( appearanceItem ) {
        template = appearanceItem["label"]["template"]
      }
    }

    return template;
  }

  renderTemplate(template, data) {
    let inject = (str, obj) => str.replace(/\${(.*?)}/g, (x, g) => {
      try {
        let value = obj;
        for ( let segment of g.split(".") ) {
          value = value[segment];
        }
        if ( undefined === value ) {
          throw "undefined";
        }
        return value;
      } catch (err) {
        return "<?>";
      }
    });
    return inject(template, data);
  }

  /**
   * Component
   */

  componentDidMount() {
    this.createGraph();
  }

  componentWillUnmount() {
    if ( this.unsubscribeTheme ) {
      this.unsubscribeTheme();
    }
    if ( this.network ) {
      this.network.destroy();
      this.network = null;
    }
  }

  render() {
    // Presence of nodes decides what is drawn OVER the canvas, never whether the canvas has a box:
    // a container sized by this state is measured at 0x0 by the fit above.
    const hasContent = this.nodes.length > 0;

    return (
      <div className="explore-preview-graph-stage">
        <div id="explore-preview-graph" className="explore-preview-graph" />

        {!hasContent &&
          <div className="explore-preview-graph-empty">
            <Alert variant="secondary">
              No content available to render. <br/>
              <br/>
              Run query to fetch some data for visualization. <br/>
              <br/>
              Start with something as simple as <br/>
              <code>SELECT FROM E LIMIT 25</code> <br/>
              to get a sense for what is available.
            </Alert>
          </div>
        }
      </div>
    );
  }
}
